<template>
  <div class="page-website-admin">
    <PageHeader>
      <div class="heading">
        <h1>
          Website Moderation
        </h1>
      </div>
    </PageHeader>

    <div class="page-columns">
      <div class="col main">
        <div class="page-content">
           <Notification
            v-if="notification.active"
            :withinOverlay="true"
            :content="notification.content"
            :type="notification.type"/>

          <div class="moderation-controls">
            <Btn
              subtle
              :class="{'is-active': !moderatedState}"
              @click="changeModeratedState(false)">
              Needs Moderation
            </Btn>
            <Btn
              subtle
              :class="{'is-active': moderatedState}"
              @click="changeModeratedState(true)">
              Moderated
            </Btn>
          </div>

          <table-data
            :rows="sites"
            :headings="[
              {key: 'site', heading: 'Website Name'},
              {key: 'accept', heading: 'Accept'},
              {key: 'decline', heading: 'Decline'}
            ]"
          >
            <div
              v-for="(site, index) in sites"
              :slot="'td-' + index + '-site'"
              :key="index"
            >
              <a target="_blank" :href="siteLink(site)">
                {{site.name}}
              </a>
            </div>

            <div
              v-for="(site, index) in sites"
              :slot="'td-' + index + '-accept'"
              :key="index"
            >
              <Btn
                v-if="!moderatedState"
                @click="acceptSite(site.id)"
                class="btn-moderate accept-site">
                <Sprite name="tick"/>
              </Btn>
            </div>

            <div
              v-for="(site, index) in sites"
              :slot="'td-' + index + '-decline'"
              :key="index"
            >
              <Btn @click="activateDecline(site)" class="btn-moderate decline-site">
                <Sprite name="close"/>
              </Btn>
            </div>
          </table-data>

          <pagination @changePage="changePage" :pagination="pageData">
          </pagination>

          <Confirm
            v-if="confirmActive"
            @confirm="declineSite"
            @cancel="cancel"
            :message="declineMessage"
            :link="{name: 'admin-websites'}"
          />
        </div>
      </div>

      <div class="col side">
        <PageSummary>
          <div>
            <h1>Website Moderation</h1>
            <p v-if="moderatedState">
              The sites listed have been moderated and were accepted.
            </p>
            <div v-else>
              <p>The sites listed have <strong>not</strong> been moderated and should be as soon as possible. Declining a website will immediately remove the website and the associated subscription.</p>
              <p><strong>Important:</strong> Please remember to log the reason internally for any declines!</p>
            </div>
          </div>
        </PageSummary>
      </div>
    </div>
  </div>
</template>

<script>
import page from '@/mixins/page'
import pagination from '@/mixins/pagination'
import confirm from '@/mixins/confirm'
import notification from '@/mixins/notification'

import {readSitesData, moderateSiteData} from '@/lib/sites'

import Table from '@/components/Table'
import PageHeader from '@/components/PageHeader'
import PageSummary from '@/components/PageSummary'
import Btn from '@/components/Btn'
import Sprite from '@/components/Sprite'

export default {
  name: 'admin-websites',

  mixins: [page, pagination, confirm, notification],

  components: {
    TableData: Table,
    PageHeader,
    PageSummary,
    Btn,
    Sprite
  },

  data() {
    return {
      sites: [],
      moderatedState: false,
      moderatingSite: {},
      title: 'Website Moderation'
    }
  },

  methods: {
    getData() {
      readSitesData(this.pagination, this.query).then(
        ({data, page}) => {
          this.sites = data
          this.pageData = page
        }
      )
    },

    cancel() {
      this.confirmActive = false
    },

    activateDecline(site) {
      this.confirmActive = true
      this.moderatingSite = site
    },

    changeModeratedState(value) {
      this.moderatedState = value
      this.getData()
    },

    acceptSite(siteId) {
      this.moderateSite(siteId, 'accept', 'moderated')
    },

    declineSite() {
      this.moderateSite(this.moderatingSite.id, 'decline', 'deleted')
    },

    moderateSite(siteId, action, text) {
      this.confirmActive = false

      moderateSiteData(siteId, action).then(() => {
        this.triggerNotification('success', 'website', text)
        this.getData()
      })
    }
  },

  computed: {
    hasSites() {
      return !!this.sites.length
    },

    declineMessage() {
      return `Are you sure you want to decline this website? This will permanantely delete <strong>${this.moderatingSite.name}</strong>!`
    },

    siteLink() {
      return (site) => {
        return `/admin/${site.company_id}/websites/${site.id}/pages`
      }
    },

    query() {
      return `?moderated=${this.moderatedState}`
    }
  },

  mounted() {
    this.getData()
  },

  watch: {
    $route() {
      this.getData()
    }
  }
}
</script>

<style lang="sass">
@import '../stylesheets/base/variables'
@import '../stylesheets/helpers/mixins'
@import '../stylesheets/helpers/extends'

.page-website-admin
  @extend %page

.btn-moderate
  border-radius: 5px

  .btn
    padding: 5px 10px

  .sprite
    width: 15px
    height: 15px

.accept-site
  background: #7eaa7b

.decline-site
  background: #de6060

.moderation-controls
  margin-bottom: $gap-medium

  .btn-wrapper
    margin-right: $gap-small

    &.is-active
      border-radius: 5px

      @include themify($themes)
        background: themed('color-secondary')
</style>
